import { Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';

import { AmostraArrasteSheet } from '../models/amostra-arraste-sheet.model';
import {
  CLASSE_SHEET_FECHADO_POR_ARRASTE, CLASSE_SHEET_INFERIOR, CURVA_SHEET, DESLOCAMENTO_MINIMO_VELOCIDADE_SHEET, DURACAO_FECHAR_SHEET_MS,
  DURACAO_VOLTAR_SHEET_MS, FRACAO_ALTURA_FECHAR_SHEET, JANELA_VELOCIDADE_SHEET_MS, LARGURA_MAXIMA_SHEET_PX, LIMIAR_INICIO_ARRASTE_SHEET,
  OPACIDADE_MINIMA_FUNDO_SHEET,
  SELETOR_IGNORAR_ARRASTE_SHEET, VELOCIDADE_FECHAR_SHEET
} from './arrastar-fechar-sheet.constantes';

@Directive({
  selector: '[appArrastarFecharSheet]',
  standalone: true
})
export class ArrastarFecharSheetDirective implements OnInit, OnDestroy {
  @Input() arrastarHabilitado = true;
  @Input() painelProprio = false;
  @Output() arrastouParaFechar = new EventEmitter<void>();

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly dialogRef = inject(MatDialogRef, { optional: true });
  private readonly dialog = inject(MatDialog);

  private inicio: { x: number; y: number } | null = null;
  private base = 0;
  private deslocamento = 0;
  private arrastando = false;
  private fechando = false;
  private engolirClique = false;
  private rolagem: HTMLElement | null = null;
  private ponteiroMouse: number | null = null;
  private amostras: AmostraArrasteSheet[] = [];
  private remover?: () => void;

  ngOnInit(): void {
    const host = this.elementRef.nativeElement;
    this.zone.runOutsideAngular(() => {
      const tocar = (e: TouchEvent) => e.touches.length === 1 && this.iniciar(e.touches[0].clientX, e.touches[0].clientY, e.target);
      const moverToque = (e: TouchEvent) => this.mover(e.touches[0]?.clientX ?? 0, e.touches[0]?.clientY ?? 0, e);
      const soltarToque = () => this.soltar();
      const pressionar = (e: PointerEvent) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        this.ponteiroMouse = e.pointerId;
        this.iniciar(e.clientX, e.clientY, e.target);
      };
      const moverMouse = (e: PointerEvent) => e.pointerId === this.ponteiroMouse && this.mover(e.clientX, e.clientY, e);
      const soltarMouse = (e: PointerEvent) => {
        if (e.pointerId !== this.ponteiroMouse) return;
        this.ponteiroMouse = null;
        this.soltar();
      };
      const clique = (e: Event) => {
        if (!this.engolirClique) return;
        this.engolirClique = false;
        e.stopPropagation();
        e.preventDefault();
      };
      host.addEventListener('touchstart', tocar, { passive: true });
      host.addEventListener('touchmove', moverToque, { passive: false });
      host.addEventListener('touchend', soltarToque, { passive: true });
      host.addEventListener('touchcancel', soltarToque, { passive: true });
      host.addEventListener('pointerdown', pressionar, { passive: true });
      window.addEventListener('pointermove', moverMouse, { passive: false });
      window.addEventListener('pointerup', soltarMouse, { passive: true });
      host.addEventListener('click', clique, true);
      this.remover = () => {
        host.removeEventListener('touchstart', tocar);
        host.removeEventListener('touchmove', moverToque);
        host.removeEventListener('touchend', soltarToque);
        host.removeEventListener('touchcancel', soltarToque);
        host.removeEventListener('pointerdown', pressionar);
        window.removeEventListener('pointermove', moverMouse);
        window.removeEventListener('pointerup', soltarMouse);
        host.removeEventListener('click', clique, true);
      };
    });
  }

  ngOnDestroy(): void {
    this.remover?.();
  }

  private get painel(): HTMLElement | null {
    const host = this.elementRef.nativeElement;
    return this.painelProprio ? host : host.closest<HTMLElement>('.cdk-overlay-pane');
  }

  private get fundo(): HTMLElement | null {
    if (this.painelProprio) return null;
    const anterior = this.painel?.parentElement?.previousElementSibling as HTMLElement | null;
    return anterior?.classList.contains('cdk-overlay-backdrop') ? anterior : null;
  }

  private iniciar(x: number, y: number, alvo: EventTarget | null): void {
    const painel = this.painel;
    if (!this.arrastarHabilitado || this.fechando || !painel || !this.ehSheetInferior(painel)) return;
    const elemento = alvo as HTMLElement | null;
    if (!elemento?.closest || elemento.closest(SELETOR_IGNORAR_ARRASTE_SHEET)) return;
    if (!this.painelProprio && elemento.closest('.cdk-overlay-pane') !== painel) return;
    this.inicio = { x, y };
    this.arrastando = false;
    this.deslocamento = 0;
    this.amostras = [];
    this.rolagem = this.encontrarRolagem(elemento, painel);
  }

  private ehSheetInferior(painel: HTMLElement): boolean {
    return this.painelProprio ? window.innerWidth <= LARGURA_MAXIMA_SHEET_PX : painel.classList.contains(CLASSE_SHEET_INFERIOR);
  }

  private mover(x: number, y: number, evento: Event): void {
    const inicio = this.inicio;
    if (!inicio) return;
    const dx = x - inicio.x;
    const dy = y - inicio.y;

    if (!this.arrastando) {
      if (Math.abs(dx) < LIMIAR_INICIO_ARRASTE_SHEET && Math.abs(dy) < LIMIAR_INICIO_ARRASTE_SHEET) return;
      if (dy <= 0 || Math.abs(dx) > Math.abs(dy) || (this.rolagem && this.rolagem.scrollTop > 0)) {
        this.inicio = null;
        return;
      }
      this.arrastando = true;
      this.base = y;
      this.prepararArraste();
    }

    if (evento.cancelable) evento.preventDefault();
    this.deslocamento = Math.max(0, y - this.base);
    this.registrarAmostra(y);
    this.aplicarDeslocamento(this.deslocamento);
  }

  private soltar(): void {
    const estavaArrastando = this.arrastando;
    this.inicio = null;
    this.arrastando = false;
    if (!estavaArrastando) return;

    this.engolirClique = true;
    setTimeout(() => (this.engolirClique = false), 400);

    const altura = this.painel?.getBoundingClientRect().height ?? window.innerHeight;
    const velocidade = this.calcularVelocidade();
    const fechar = this.deslocamento > altura * FRACAO_ALTURA_FECHAR_SHEET
      || (velocidade > VELOCIDADE_FECHAR_SHEET && this.deslocamento > DESLOCAMENTO_MINIMO_VELOCIDADE_SHEET);
    if (fechar) {
      this.animarFechamento();
    } else {
      this.animarRetorno();
    }
  }

  private prepararArraste(): void {
    const painel = this.painel;
    const fundo = this.fundo;
    if (painel) {
      painel.style.transition = 'none';
      painel.style.willChange = 'transform';
    }
    if (fundo) fundo.style.transition = 'none';
  }

  private aplicarDeslocamento(deslocamento: number): void {
    const painel = this.painel;
    if (!painel) return;
    painel.style.transform = `translate3d(0, ${deslocamento}px, 0)`;
    const fundo = this.fundo;
    if (fundo) {
      const progresso = Math.min(1, deslocamento / Math.max(1, painel.getBoundingClientRect().height));
      fundo.style.opacity = String(Math.max(OPACIDADE_MINIMA_FUNDO_SHEET, 1 - progresso));
    }
  }

  private animarFechamento(): void {
    const painel = this.painel;
    if (!painel) return;
    this.fechando = true;
    const fundo = this.fundo;
    painel.style.transition = `transform ${DURACAO_FECHAR_SHEET_MS}ms ${CURVA_SHEET}`;
    painel.style.transform = 'translate3d(0, 100%, 0)';
    if (fundo) {
      fundo.style.transition = `opacity ${DURACAO_FECHAR_SHEET_MS}ms ${CURVA_SHEET}`;
      fundo.style.opacity = '0';
    }
    setTimeout(() => {
      painel.classList.add(CLASSE_SHEET_FECHADO_POR_ARRASTE);
      this.zone.run(() => {
        if (this.arrastouParaFechar.observed) {
          this.emitirFechamento(painel);
        } else {
          this.dialogRef?.close();
        }
      });
    }, DURACAO_FECHAR_SHEET_MS);
  }

  private emitirFechamento(painel: HTMLElement): void {
    const abertosAntes = this.dialog.openDialogs.length;
    this.arrastouParaFechar.emit();
    const abertos = this.dialog.openDialogs;
    const confirmacao = abertos.length > abertosAntes ? abertos[abertos.length - 1] : null;
    if (confirmacao) {
      confirmacao.afterClosed().subscribe(() => setTimeout(() => this.restaurarSeContinuouAberto(painel)));
      return;
    }
    this.restaurarSeContinuouAberto(painel);
  }

  private restaurarSeContinuouAberto(painel: HTMLElement): void {
    if (!(this.dialogRef instanceof MatDialogRef) || this.dialogRef.getState() !== MatDialogState.OPEN) return;
    painel.classList.remove(CLASSE_SHEET_FECHADO_POR_ARRASTE);
    this.fechando = false;
    this.animarRetorno();
  }

  private animarRetorno(): void {
    const painel = this.painel;
    if (!painel) return;
    const fundo = this.fundo;
    painel.style.transition = `transform ${DURACAO_VOLTAR_SHEET_MS}ms ${CURVA_SHEET}`;
    painel.style.transform = 'translate3d(0, 0, 0)';
    if (fundo) {
      fundo.style.transition = `opacity ${DURACAO_VOLTAR_SHEET_MS}ms ${CURVA_SHEET}`;
      fundo.style.opacity = '';
    }
    setTimeout(() => {
      if (this.arrastando || this.fechando) return;
      painel.style.transition = '';
      painel.style.transform = '';
      painel.style.willChange = '';
      if (fundo) fundo.style.transition = '';
    }, DURACAO_VOLTAR_SHEET_MS);
  }

  private registrarAmostra(y: number): void {
    const agora = performance.now();
    this.amostras.push({ y, tempo: agora });
    while (this.amostras.length > 2 && agora - this.amostras[0].tempo > JANELA_VELOCIDADE_SHEET_MS) this.amostras.shift();
  }

  private calcularVelocidade(): number {
    const primeira = this.amostras[0];
    const ultima = this.amostras[this.amostras.length - 1];
    if (!primeira || !ultima || ultima.tempo === primeira.tempo) return 0;
    return (ultima.y - primeira.y) / (ultima.tempo - primeira.tempo);
  }

  private encontrarRolagem(elemento: HTMLElement, painel: HTMLElement): HTMLElement | null {
    let atual: HTMLElement | null = elemento;
    while (atual && atual !== painel) {
      const estilo = getComputedStyle(atual);
      const rolavel = /(auto|scroll)/.test(estilo.overflowY) && atual.scrollHeight > atual.clientHeight + 1;
      if (rolavel) return atual;
      atual = atual.parentElement;
    }
    return null;
  }
}
